import styles from './MultiCollectionModal.module.scss';
import Modal from 'src/components/modal/Modal';
import Button from 'src/components/button/Button';
import { TextInput, TextInputValue } from 'src/components/text-input/TextInput';
import Checkbox from 'src/components/checkbox/Checkbox';
import { useCallback, useEffect, useState } from 'react';
import Spinner from '@src/components/spinner/Spinner';
import { AustraliaStandaloneLessonAgeGroupOptions, NorthAmericaStandaloneLessonAgeGroupOptions } from '@src/constants';
import RadioGroup from '@src/components/radio-group/RadioGroup';
import { MultiCollectionType, useCreateMultiCollectionMutation, useUpdateMultiCollectionMutation } from '@src/api/graphql-codegen';
import { useToast } from '@src/hooks';
import SelectMulti from '@src/components/select-multi/SelectMulti';
import { isJSON } from '@src/utils/validation';
import { logger } from '../../../../utils/logger';

type Props = {
  open: boolean;
  onClose: () => void;
  collection?: any;
};

const ValidationErrors = {
  empty: 'Please fill in the field.',
}

const countryOptions = [
  {label: "Australia", value: "Australia"},
  {label: "Canada & USA", value: "Canada & USA"},
]

function MultiCollectionModal(props: Props) {
  const { open, collection, onClose} = props;
  const [ name, setName ] = useState(collection ? collection.name : "")
  const [ description, setDescription ] = useState(collection ? collection.description : "")
  const [ error, setError ] = useState("")
  const [ country, setCountry] = useState(collection ? collection.country : countryOptions[0].value)
  const [ ageGroup, setAgeGroup ] = useState<string[]>([])
  const [ hidden, setHidden ] = useState(collection?.hidden || false);

  const { successToast, errorToast } = useToast();

  useEffect(() => {
    if (collection) {
     if (isJSON(collection?.grade)) {
      setAgeGroup(JSON.parse(collection.grade ?? '[]') as string[])
    } else {
      if (collection?.grade) {
        setAgeGroup([`${collection.grade}`]);  
      }
    }
    }
  }, [collection])

  const [createMultiCollection, {loading: creating}] = useCreateMultiCollectionMutation({
    onCompleted(data, clientOptions) {
      if (data.createMultiCollection) {
        successToast("Multi Collection has been created successfully!");
        onClose();
      } else {
        errorToast("Create failed, Please try again!");
        onClose();
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    }
  })

  const [updateMultiCollection, {loading: updating}] = useUpdateMultiCollectionMutation({
    onCompleted(data, clientOptions) {
      if (data.updateMultiCollection) {
        successToast("Multi Collection has been updated successfully!");
        onClose();
      } else {
        errorToast("Update failed, Please try again!");
        onClose();
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    }
  })

  const isEdit = !!collection
  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    const onPageLoad = () => {
      const item = document.getElementById("name")
      if (item) {
        item.focus();
      }
    };
    if (document.readyState === 'complete') {
      onPageLoad();
    } else {
      window.addEventListener('load', onPageLoad, false);
      return () => window.removeEventListener('load', onPageLoad);
    }

  }, [])

  const handleChange = (value: TextInputValue) => {
    setName(value.toString())
    setError('')
  }
  const handleChangeDescription = (value: TextInputValue) => {
    setDescription(value.toString())
    setError('')
  }
  const handleCreateNew = useCallback(() => {
    if (ageGroup.length === 0) {
      setError(ValidationErrors.empty);
      return
    }
    if (collection) {
      updateMultiCollection({
        variables: {
          input: {
            id: collection.id,
            name,
            country,
            grade: JSON.stringify(ageGroup),
            description: description,
            type: MultiCollectionType.NORMAL,
            hidden: hidden
          },
        }
      });
    } else {
      createMultiCollection({
        variables: {
          input: {
            name,
            country,
            grade: JSON.stringify(ageGroup),
            description: description,
            type: MultiCollectionType.NORMAL,
            hidden: hidden
          },
        }
      });
    }
  }, [ageGroup, name, description, country, collection, hidden, createMultiCollection, updateMultiCollection])

  const onChangeAgeGroup = useCallback((options: string[]) => {
    setAgeGroup(options);
  }, []);

  const handleChangeCountry = useCallback((v: string) => {
    setCountry(v);
  }, []);

  return (
    <Modal open={open} onClose={handleClose} className={styles.wrapper}>
      <div>
        <div className={styles.title}>{isEdit ? 'Edit Multi-Collection' : 'Create Multi-Collection'}</div>
        <div className={styles.content}>
          <div>
            <div className={styles.subTitle}>Name</div>
            <TextInput id="name" placeholder={'Add Multi Collection Name'} value={name} label={''} onChange={handleChange} />
          </div>
          <div>
            <div className={styles.subTitle}>Description</div>
            <TextInput id="description" placeholder={'Add description'} value={description} label={''} onChange={handleChangeDescription} />
          </div>
          <div>
            <div className={styles.subTitle}>Country</div>
            <RadioGroup
              value={country}
              options={countryOptions}
              onChange={handleChangeCountry}
              name={''}
              hasError={false}
              subTitle={""}
            />
          </div>
          <div>
            <div className={styles.subTitle}>Other</div>
            <Checkbox id='hide_multi_collection' label='Hide Multi-Collection' checked={hidden} className={styles.itemView} onChange={() => setHidden(!hidden)} />
          </div>
          <div className={styles.collectionSelect}>
            <div className={styles.subTitle}>Age Group</div>
            <SelectMulti
              inline={false}
              doNotSort={true} 
              value={ageGroup}
              onChange={onChangeAgeGroup}
              label={''}
              options={ country === countryOptions[0].value ? AustraliaStandaloneLessonAgeGroupOptions : NorthAmericaStandaloneLessonAgeGroupOptions }
              showSearch={false}
              menuClassName={styles.selectorStyle}
              error={!!error}
              subTitle={error}
            />
          </div>
        </div>  
        <div className={styles.action}>
            <Button disabled={creating || updating} size="small" leftIcon={(creating || updating) ? <Spinner width={20} height={20} /> : undefined} className={styles.cancelButton} onClick={onClose} label={'CANCEL'}/>
            <Button disabled={creating || updating || !name || !ageGroup} size="small" leftIcon={(creating || updating) ? <Spinner width={20} height={20} /> : undefined} className={styles.createButton} onClick={handleCreateNew} label={isEdit ? 'UPDATE' : 'CREATE'}/>
          </div>      
      </div>
    </Modal>
  );
}

export default MultiCollectionModal;
